// Wallet balance, saved card, and top-ups — thin adapters over valoi_engine's
// authenticated /api/v1/credits/* proxy (which fronts the valoi-billing service).
//
// Identity comes from the Bearer JWT via apiClient (same as every other real
// call), so none of these take a userId. Each function is a single getJson/postJson
// plus a snake_case -> camelCase mapping, mirroring authService.fetchMe(). The
// wallet is denominated in credits (1 credit = $1 = 100 cents; cents live only
// inside billing/engine); top-ups alone speak USD, because that's what the card
// is charged — and under the flat-price model the credits received equal the
// dollars paid, exactly.
//
// Note: card save and top-up crediting are ASYNCHRONOUS on the billing side
// (persisted by Stripe webhooks), so callers poll getPaymentMethod()/getBalance()
// after a successful Stripe confirmation rather than trusting an immediate read.

import { deleteJson, getJson, postJson, putJson } from '@/shared/api/apiClient';
import { logger } from '@/shared/utils/logger';
import type {
  BillingAddress,
  PaymentMethodSummary,
  TopUpResult,
  TopUpStatus,
  TransactionPage,
  WalletBalance,
  WalletTransactionKind,
} from '@/shared/types';

const CREDITS_BASE = '/api/v1/credits';

// --- Wire shapes (local — only cross-file types live in types.ts) ------------

// credits_on_hold/credits_total are optional on the wire so the app keeps working
// against an engine deploy that predates them (degrades to "nothing on hold").
type BalanceApiResponse = {
  credits: number;
  credits_on_hold?: number;
  credits_total?: number;
};

type PaymentMethodApiItem = {
  id: string | null;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
};

type PaymentMethodsApiResponse = {
  payment_methods: PaymentMethodApiItem[];
  updated_at?: string | null;
};

type SetupIntentApiResponse = { client_secret: string };

type TransactionApiItem = {
  id: string;
  kind: string;
  amount_credits: number;
  occurred_at: string;
  reference: string | null;
  note: string | null;
};

type TransactionsApiResponse = {
  transactions: TransactionApiItem[];
  next_cursor: string | null;
};

type TopUpApiResponse = {
  charge_id: string;
  status: string;
  amount_usd: number;
  client_secret: string | null;
};

type BillingAddressApiResponse = {
  country: string;
  state?: string | null;
  postal_code?: string | null;
  city?: string | null;
  line1?: string | null;
};

// Mirrors NotificationProvider's id generation. Exported because there IS a
// retry-reuse path now: a top-up refused with `billing_address_required` is
// retried after the address form, and that retry must carry the SAME key. A
// fresh key would be a second, independent charge attempt — and while today's
// billing refuses at the tax step before any charge exists, relying on that
// ordering to prevent a double charge is exactly the kind of assumption that
// stops holding quietly.
export function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

// GET /api/v1/credits/balance -> { credits, credits_on_hold, credits_total }
// (credits, 1 credit = $1). A user who never started card setup reads back 0.0,
// not an error.
export async function getBalance(): Promise<WalletBalance> {
  const body = await getJson<BalanceApiResponse>(`${CREDITS_BASE}/balance`);
  const onHoldCredits = body.credits_on_hold ?? 0;
  return {
    balanceCredits: body.credits,
    onHoldCredits,
    // Trust the server's total when it sends one; only fall back to adding locally
    // for an older engine. Adding is right because `credits` is already net of holds.
    totalCredits: body.credits_total ?? body.credits + onHoldCredits,
  };
}

// GET /api/v1/credits/payment-methods -> { payment_methods: [...] }. At most one
// entry today (no multi-card support); returns the first, or null when none.
export async function getPaymentMethod(): Promise<PaymentMethodSummary | null> {
  const body = await getJson<PaymentMethodsApiResponse>(`${CREDITS_BASE}/payment-methods`);
  const first = body.payment_methods[0];
  if (!first) return null;
  return {
    id: first.id,
    brand: first.brand,
    last4: first.last4,
    expMonth: first.exp_month,
    expYear: first.exp_year,
    isDefault: first.is_default,
  };
}

// --- Wire-value validation ---------------------------------------------------
//
// These enum-ish fields are checked against the known sets rather than cast. A blind
// `as WalletTransactionKind` let an unrecognised value (a `refund` kind added on the
// billing side, say) flow into the UI typed as something it isn't — the row rendered
// with the wrong label and nothing anywhere reported a problem. Falling back keeps an
// unknown value displayable and honest instead of mislabelled, and says so in the log.

const TRANSACTION_KINDS: readonly WalletTransactionKind[] = ['topup', 'charge', 'adjustment'];
const TOPUP_STATUSES: readonly TopUpStatus[] = [
  'captured',
  'pending',
  'requires_action',
  'canceled',
  'failed',
];

function toTransactionKind(value: string): WalletTransactionKind {
  if ((TRANSACTION_KINDS as readonly string[]).includes(value)) {
    return value as WalletTransactionKind;
  }
  logger.warn(`Unknown wallet transaction kind from API: ${value}`);
  // 'adjustment' is the neutral bucket: it renders with the row's own note rather than
  // a kind-derived label, so an unfamiliar kind still shows the server's description.
  return 'adjustment';
}

function toTopUpStatus(value: string): TopUpStatus {
  if ((TOPUP_STATUSES as readonly string[]).includes(value)) {
    return value as TopUpStatus;
  }
  logger.warn(`Unknown top-up status from API: ${value}`);
  // 'pending' is the safe unknown: the UI neither credits the balance nor declares
  // failure, and the caller re-fetches. Guessing 'captured' would show money the user
  // may not have.
  return 'pending';
}

// GET /api/v1/credits/transactions?limit&before -> a page of curated wallet activity
// (top-ups, settled video charges, ops refunds — see WalletTransaction). Cursor-paged:
// pass the previous page's nextCursor as `before` to fetch older rows.
export async function getTransactions(params?: { limit?: number; before?: string }): Promise<TransactionPage> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set('limit', String(params.limit));
  if (params?.before) query.set('before', params.before);
  const qs = query.toString();
  const body = await getJson<TransactionsApiResponse>(`${CREDITS_BASE}/transactions${qs ? `?${qs}` : ''}`);
  return {
    transactions: body.transactions.map((t) => ({
      id: t.id,
      kind: toTransactionKind(t.kind),
      amountCredits: t.amount_credits,
      occurredAt: t.occurred_at,
      reference: t.reference,
      note: t.note,
    })),
    nextCursor: body.next_cursor,
  };
}

// DELETE /api/v1/credits/payment-method -> 204. Detaches the customer's saved card
// (single-card model, so no id in the path). Idempotent server-side: removing when
// nothing is on file still succeeds. Billing clears its cached brand/last4 too, so a
// subsequent getPaymentMethod() returns null.
export async function deletePaymentMethod(): Promise<void> {
  await deleteJson<void>(`${CREDITS_BASE}/payment-method`);
}

// POST /api/v1/credits/setup-intent -> { client_secret }. Feed the secret to
// stripe.confirmCardSetup() to attach the card client-side; billing persists the
// brand/last4 asynchronously via its setup_intent.succeeded webhook.
export async function createSetupIntent(): Promise<string> {
  const body = await postJson<SetupIntentApiResponse>(`${CREDITS_BASE}/setup-intent`);
  return body.client_secret;
}

// POST /api/v1/credits/topup -> { charge_id, status, amount_usd, client_secret }.
// The response never includes the new balance — re-fetch getBalance() after a
// captured charge (or after completing 3DS when status === 'requires_action').
// Non-2xx billing failures surface as ApiError (see credits_api_contract.md).
// Pass `idempotencyKey` to retry a specific attempt (see newIdempotencyKey);
// omit it for a genuinely new top-up.
export async function topUp(amountUsd: number, idempotencyKey?: string): Promise<TopUpResult> {
  const body = await postJson<TopUpApiResponse>(`${CREDITS_BASE}/topup`, {
    amount_usd: amountUsd,
    idempotency_key: idempotencyKey ?? newIdempotencyKey(),
  });
  return {
    chargeId: body.charge_id,
    status: toTopUpStatus(body.status),
    amountUsd: body.amount_usd,
    clientSecret: body.client_secret,
  };
}

// PUT /api/v1/credits/billing-address -> the stored address.
//
// PUT semantics: the whole address replaces the previous one, so the caller
// always sends every field. Only `country` is required — the rest sharpen the
// tax jurisdiction (a postal code can straddle two taxing districts).
//
// There is no read counterpart because billing exposes no GET: the flow is
// attempt the top-up, prompt on `billing_address_required`, retry.
export async function setBillingAddress(address: BillingAddress): Promise<BillingAddress> {
  const body = await putJson<BillingAddressApiResponse>(`${CREDITS_BASE}/billing-address`, {
    country: address.country,
    state: address.state ?? null,
    postal_code: address.postalCode ?? null,
    city: address.city ?? null,
    line1: address.line1 ?? null,
  });
  return {
    country: body.country,
    state: body.state ?? null,
    postalCode: body.postal_code ?? null,
    city: body.city ?? null,
    line1: body.line1 ?? null,
  };
}
